import { __ } from '@wordpress/i18n';
import {
  PanelBody,
  RangeControl,
  ToggleControl,
  SelectControl,
  __experimentalUnitControl as UnitControl,
} from '@wordpress/components';

import {
  SliderDotsColorsControls,
  SliderNavColorsControls,
} from '../../../../utils/slider-controls-panel';

/*
 * The panel's share of the row is also the parallax rate: its content slides
 * its own inner width while the media slides a full column, so a narrower
 * panel lags further behind. That is why there is no separate "parallax
 * strength" — two controls for one relationship would only let an author
 * desync it from the layout in front of them.
 */
const PANEL_MIN = 25;
const PANEL_MAX = 60;

/*
 * Three compositions over the same regions. The edge preview only exists in
 * `editorial` — overlay floats the panel on top of the photo and stacked puts
 * it underneath, and neither leaves anywhere for a third column to be.
 */
const LAYOUTS = [
  { label: 'Editorial (panel beside media)', value: 'editorial' },
  { label: 'Overlay (panel over media)', value: 'overlay' },
  { label: 'Stacked (panel below media)', value: 'stacked' },
];

export default function LayoutPanel({ attributes, setAttributes }) {
  const {
    layout,
    panelSide,
    panelWidth,
    mediaHeight,
    showPreview,
    previewPeek,
    settle,
    showArrows,
    showCounter,
  } = attributes;

  return (
    <PanelBody title={__('Layout', 'brandy-blocks')} initialOpen={true}>
      <SelectControl
        label={__('Layout', 'brandy-blocks')}
        value={layout}
        options={LAYOUTS.map((entry) => ({
          label: __(entry.label, 'brandy-blocks'),
          value: entry.value,
        }))}
        onChange={(value) => setAttributes({ layout: value })}
      />
      {layout !== 'stacked' && (
        <SelectControl
          label={__('Panel side', 'brandy-blocks')}
          value={panelSide}
          options={[
            { label: __('Right', 'brandy-blocks'), value: 'right' },
            { label: __('Left', 'brandy-blocks'), value: 'left' },
          ]}
          onChange={(value) => setAttributes({ panelSide: value })}
        />
      )}
      <RangeControl
        label={__('Panel width (%)', 'brandy-blocks')}
        help={__(
          'Also sets how far the panel text lags behind the media.',
          'brandy-blocks'
        )}
        value={panelWidth}
        min={PANEL_MIN}
        max={PANEL_MAX}
        onChange={(value) => setAttributes({ panelWidth: value })}
      />
      <UnitControl
        label={__('Media height', 'brandy-blocks')}
        value={mediaHeight}
        onChange={(value) => setAttributes({ mediaHeight: value || '540px' })}
        units={[
          { value: 'px', label: 'px' },
          { value: 'vh', label: 'vh' },
          { value: 'rem', label: 'rem' },
        ]}
      />
      {layout === 'editorial' && (
        <ToggleControl
          label={__('Preview next slide at the edge', 'brandy-blocks')}
          help={__(
            'Repeats the media strip past the panel, one slide ahead.',
            'brandy-blocks'
          )}
          checked={showPreview}
          onChange={(value) => setAttributes({ showPreview: value })}
        />
      )}
      {layout === 'editorial' && showPreview && (
        <RangeControl
          label={__('Preview width (%)', 'brandy-blocks')}
          help={__(
            'How much of the next slide stays visible past the panel.',
            'brandy-blocks'
          )}
          value={previewPeek}
          min={0}
          max={25}
          onChange={(value) => setAttributes({ previewPeek: value })}
        />
      )}
      <ToggleControl
        label={__('Settle the media on arrival', 'brandy-blocks')}
        checked={settle}
        onChange={(value) => setAttributes({ settle: value })}
      />
      <ToggleControl
        label={__('Arrows', 'brandy-blocks')}
        checked={showArrows}
        onChange={(value) => setAttributes({ showArrows: value })}
      />
      <ToggleControl
        label={__('Counter', 'brandy-blocks')}
        checked={showCounter}
        onChange={(value) => setAttributes({ showCounter: value })}
      />
      {/* Colours only. This block pins its own nav and dots styles — the
          counter and the edge-preview strip are built around them — so the
          variant dropdowns the other sliders carry would offer a choice it
          ignores. */}
      {showArrows && (
        <SliderNavColorsControls
          attributes={attributes}
          setAttributes={setAttributes}
        />
      )}
      {/* The counter IS this block's dots style (`fraction`, pinned in
          render.php), so its colour comes from the dots axis — only the active
          key has anything to paint here. */}
      {showCounter && (
        <SliderDotsColorsControls
          attributes={attributes}
          setAttributes={setAttributes}
        />
      )}
    </PanelBody>
  );
}
